import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useI18n } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t, productName } = useI18n();
  const [personnelNumber, setPersonnelNumber] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [quickLoading, setQuickLoading] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(personnelNumber, password);
      setLoggedInUser(user);
    } catch (err) {
      setError(err.response?.data?.detail || t('login.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleQuickCheckIn = async (location) => {
    setQuickLoading(location);
    try {
      await api.post('/time/check-in', { work_location: location });
      navigate('/dashboard');
    } catch (err) {
      const msg = err.response?.data?.detail || t('login.checkInError');
      if (msg.includes('Bereits eingestempelt')) {
        navigate('/dashboard');
      } else {
        setError(msg);
        setQuickLoading('');
      }
    }
  };

  const handleQuickCheckOut = async () => {
    setQuickLoading('CHECKOUT');
    try {
      await api.post('/time/check-out', {});
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t('login.checkOutError'));
      setQuickLoading('');
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-dark">
      <div className="card shadow-lg" style={{ width: '100%', maxWidth: 420 }}>
        <div className="card-body p-4">
          <div className="d-flex justify-content-end mb-2">
            <LanguageSwitcher variant="outline-secondary" />
          </div>
          <div className="text-center mb-4">
            <i className="bi bi-clock-history fs-1 text-primary" />
            <h2 className="mt-2 fw-bold">{productName}</h2>
            <p className="text-muted">{t('app.tagline')}</p>
          </div>

          {!loggedInUser ? (
            <form onSubmit={handleLogin}>
              <div className="mb-3">
                <label className="form-label">{t('login.personnelNumber')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={personnelNumber}
                  onChange={(e) => setPersonnelNumber(e.target.value)}
                  placeholder={t('login.personnelNumberPlaceholder')}
                  required
                  autoFocus
                />
              </div>
              <div className="mb-3">
                <label className="form-label">{t('login.password')}</label>
                <input
                  type="password"
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                {t('login.submit')}
              </button>
            </form>
          ) : (
            <div>
              <div className="alert alert-success text-center">
                <i className="bi bi-check-circle me-2" />
                {t('login.greeting', { name: loggedInUser.first_name })}
              </div>
              <p className="text-center text-muted mb-3">{t('login.whatToDo')}</p>

              <div className="d-grid gap-2">
                <button
                  className="btn btn-success btn-lg"
                  onClick={() => handleQuickCheckIn('OFFICE')}
                  disabled={!!quickLoading}
                >
                  {quickLoading === 'OFFICE'
                    ? <span className="spinner-border spinner-border-sm me-2" />
                    : <i className="bi bi-building me-2" />}
                  {t('login.checkInOffice')}
                </button>
                <button
                  className="btn btn-info btn-lg text-white"
                  onClick={() => handleQuickCheckIn('HOME_OFFICE')}
                  disabled={!!quickLoading}
                >
                  {quickLoading === 'HOME_OFFICE'
                    ? <span className="spinner-border spinner-border-sm me-2" />
                    : <i className="bi bi-house me-2" />}
                  {t('login.checkInHome')}
                </button>
                <button
                  className="btn btn-danger btn-lg"
                  onClick={handleQuickCheckOut}
                  disabled={!!quickLoading}
                >
                  {quickLoading === 'CHECKOUT'
                    ? <span className="spinner-border spinner-border-sm me-2" />
                    : <i className="bi bi-door-open me-2" />}
                  {t('login.checkOut')}
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/dashboard')}
                >
                  <i className="bi bi-grid me-2" />
                  {t('login.toOverview')}
                </button>
              </div>
              {error && <div className="alert alert-danger mt-3 py-2">{error}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
